import { Component } from '@theme/component';

/**
 * @typedef {{
 *   trigger: HTMLButtonElement;
 *   overlay: HTMLDivElement;
 *   panel: HTMLDivElement;
 * }} LegacyRegionSelectorRefs
 *
 * @extends {Component<LegacyRegionSelectorRefs>}
 */
class LegacyRegionSelectorComponent extends Component {
  requiredRefs = ['panel', 'overlay', 'trigger'];

  #isOpen = false;

  toggle = () => (this.#isOpen ? this.close() : this.open());

  open = () => {
    if (this.#isOpen) return;
    if (document.body.classList.contains('region-selector-menu--opened')) return;
    this.#isOpen = true;
    this.setAttribute('data-show-region-selector-menu', 'true');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.refs.overlay.removeAttribute('hidden');
    document.body.dataset.legacyRegionSelectorScrollY = String(window.scrollY);
    document.body.classList.add('region-selector-menu--opened');
    const firstFocusable = this.refs.panel.querySelector('a, button');
    firstFocusable?.focus();
  };

  close = () => {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.setAttribute('data-show-region-selector-menu', 'false');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    const y = Number(document.body.dataset.legacyRegionSelectorScrollY ?? 0);
    delete document.body.dataset.legacyRegionSelectorScrollY;
    window.scrollTo(0, y);
    this.refs.trigger.focus();
    // Defer overlay hide + body-class removal until the panel finishes its
    // close transition. Desktop variant slides (transform); mobile variant
    // fades (opacity). Listen for whichever property the variant actually
    // animates so the cleanup fires in both cases — keying off only
    // `transform` left the body class stuck on mobile and blocked the next
    // open() via the body-class guard.
    this.refs.panel.addEventListener(
      'transitionend',
      (e) => {
        if (e.propertyName !== 'transform' && e.propertyName !== 'opacity') return;
        if (this.#isOpen) return;
        this.refs.overlay.setAttribute('hidden', '');
        document.body.classList.remove('region-selector-menu--opened');
      },
      { once: true }
    );
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#isOpen) {
      this.#isOpen = false;
      document.body.classList.remove('region-selector-menu--opened');
      delete document.body.dataset.legacyRegionSelectorScrollY;
    }
  }
}

if (!customElements.get('legacy-region-selector-component')) {
  customElements.define('legacy-region-selector-component', LegacyRegionSelectorComponent);
}
